import React from 'react'
import styled from '@emotion/styled'
import { Constants } from '@mobi/settings'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { LoadingSpinner } from '@mobi/component-library/Feedback/LoadingSpinner'
import { Toggle } from '@mobi/component-library/Selection&Input/Toggle'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { SvgLazy } from '@mobi/component-library/Assets/Svg/SvgLazy'
import { createImageBase64FromHtml } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/helpers/createImageFromHtml'

export const PreviewDisplayFrame: React.FC<{
  isError: boolean
  isLoading: boolean
  children: React.ReactNode
  shareImageElRef: React.RefObject<HTMLDivElement>
  handleShareClick: () => void
  shouldShowCost?: boolean
  handleToggleShouldShowCost?: () => void
}> = ({
  isError,
  isLoading,
  children,
  shareImageElRef,
  handleShareClick,
  handleToggleShouldShowCost,
  shouldShowCost = false,
}) => {
  const devInternalImageTestClickCountRef = React.useRef(0)
  const [devInternalImageSrcTest, setDevInternalImageSrcTest] = React.useState<string | null>(null)

  const handleDevInternalImageTestClick = () => {
    if (!shareImageElRef.current || devInternalImageTestClickCountRef.current < 10) {
      devInternalImageTestClickCountRef.current = ++devInternalImageTestClickCountRef.current
      return
    }
    createImageBase64FromHtml(shareImageElRef.current)
      .then(base64Image => {
        setDevInternalImageSrcTest(base64Image)
      })
      .catch()
  }

  return (
    <WrapperStyled>
      <div ref={shareImageElRef} className='share-bet-preview__preview-image'>
        <div onClick={handleDevInternalImageTestClick}>
          <SvgLazy name='TABtouchLogo' color={colors.white} width='12rem' />
        </div>

        <div>{children}</div>

        <p>Gamble responsibly. Call Gambling Help on {Constants.GamblingHelpPhoneNumber}</p>
      </div>

      {devInternalImageSrcTest && (
        <div className='share-bet-preview__dev-image-test'>
          <pre>Test Image Output</pre>
          <img src={devInternalImageSrcTest} />
        </div>
      )}

      {handleToggleShouldShowCost && (
        <div className='share-bet-preview__toggle-cost'>
          Show cost information
          <Toggle
            id='share-bet-cost-toggle'
            isOn={shouldShowCost}
            onToggle={handleToggleShouldShowCost}
            disabled={false}
            size='md'
            testId='share-bets-cost-toggle'
          />
        </div>
      )}

      {isError && (
        <InfoBox color='red' size='md'>
          Sorry, we&apos;re unable to create share. Please try again later.
        </InfoBox>
      )}

      <Button color='primary' size='md' isFullWidth onClick={handleShareClick} disabled={isLoading}>
        {isLoading ? (
          <LoadingSpinner size='2.2rem' color='purple' />
        ) : (
          <>
            <Icon name='LineShare02' size='1.6rem' color={colors.white} /> Share Bet
          </>
        )}
      </Button>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.sm,

  '.share-bet-preview': {
    '&__preview-image': {
      padding: `${spacing.big} 0`,
      background: colors.studio[600],
      borderRadius: radius.lgx1,

      // Branding Header
      '& > div:nth-of-type(1)': {
        boxSizing: 'border-box',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',

        svg: { maxWidth: '12rem' },
      },

      // Bet Area
      '& > div:nth-of-type(2)': {
        padding: spacing.md,

        '> *': { border: 0 },
      },

      p: {
        margin: 0,
        fontFamily: font.family.primary,
        fontSize: font.size.base.fontSize,
        fontWeight: font.weight.regular,
        lineHeight: font.size.base.lineHeight,
        textAlign: 'center',
        color: colors.studio[200],
      },
    },

    '&__dev-image-test': {
      textAlign: 'center',
      fontSize: '1.4rem',
      background: colors.surface[200],
      borderRadius: radius.md,

      img: { maxWidth: '100%' },
    },

    '&__toggle-cost': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: font.family.primary,
      fontSize: font.size.md.fontSize,
      fontWeight: font.weight.medium,
      lineHeight: font.size.md.lineHeight,
      letterSpacing: font.size.md.letterSpacing,
    },
  },
})
